import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Tooltip,
  LinearProgress,
  IconButton,
  Collapse,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { ExpandMore, ExpandLess, Memory, Storage, Speed } from '@mui/icons-material';

export interface NodeResources {
  cpu: {
    total: number;
    used: number;
    unit: string;
  };
  memory: {
    total: number;
    used: number;
    unit: string;
  };
}

export interface NodeStats {
  nodeName: string;
  nodeType: string;
  pods: {
    total: number;
    Running: number;
    ContainerStatusUnknown: number;
    OOMKilled: number;
    Completed: number;
    Error: number;
  };
  resources: NodeResources;
}

interface NodeStatusPanelProps {
  nodeStats: Record<string, NodeStats>;
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'Running':
      return 'var(--bulk-success)';
    case 'ContainerStatusUnknown':
      return '#9e9e9e';
    case 'OOMKilled':
      return 'var(--bulk-warning)';
    case 'Completed':
      return '#2196f3';
    case 'Error':
      return 'var(--bulk-error)';
    case 'Error↓':
      return '#d32f2f';
    case 'CrashLoopBackOff':
      return '#e91e63';
    case 'Terminating':
      return '#9c27b0';
    default:
      return '#9e9e9e';
  }
};

const getMachineTypeIcon = (machineType: string) => {
  if (machineType.includes('highmem')) {
    return <Memory />;
  } else if (machineType.includes('standard')) {
    return <Speed />;
  } else {
    return <Storage />;
  }
};

const formatResourceValue = (value: number, unit: string) => {
  if (unit === 'Gi') {
    return `${value.toFixed(1)} GB`;
  }
  return `${value} ${unit}`;
};

const ResourceProgressBar = ({ 
  used, 
  total, 
  unit, 
  color 
}: { 
  used: number; 
  total: number; 
  unit: string;
  color: string;
}) => {
  const percentage = (used / total) * 100;
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {formatResourceValue(used, unit)} / {formatResourceValue(total, unit)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {percentage.toFixed(1)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'background.paper',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            bgcolor: color,
          }
        }}
      />
    </Box>
  );
};

export function NodeStatusPanel({ nodeStats }: NodeStatusPanelProps) {
  const [expanded, setExpanded] = React.useState(true);

  // Calculate totals
  const totalNodes = Object.keys(nodeStats).length;
  const totalPods = Object.values(nodeStats).reduce((sum, node) => sum + node.pods.total, 0);

  return (
    <Paper
      sx={{
        mb: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}
      elevation={2}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="div">
            Node Status
          </Typography>
          <Chip
            label={`Total Nodes: ${totalNodes}`}
            color="primary"
            size="small"
          />
          <Chip
            label={`Total Pods: ${totalPods}`}
            color="primary"
            size="small"
          />
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Node Name</TableCell>
                  <TableCell>Node Type</TableCell>
                  <TableCell align="center">Pods</TableCell>
                  <TableCell align="center">CPU Usage</TableCell>
                  <TableCell align="center">Memory Usage</TableCell>
                  <TableCell>Pod Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(nodeStats).map(([nodeName, node]) => (
                  <TableRow key={nodeName}>
                    <TableCell>
                      <Typography variant="body2">
                        {nodeName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getMachineTypeIcon(node.nodeType)}
                        <Typography variant="body2">{node.nodeType}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">
                          {node.pods.total} pods
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(node.pods.total / totalPods) * 100}
                          sx={{
                            width: 100,
                            height: 4,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 2,
                            }
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ width: 150 }}>
                        <ResourceProgressBar
                          used={node.resources.cpu.used}
                          total={node.resources.cpu.total}
                          unit={node.resources.cpu.unit}
                          color="#2196f3"
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ width: 150 }}>
                        <ResourceProgressBar
                          used={node.resources.memory.used}
                          total={node.resources.memory.total}
                          unit={node.resources.memory.unit}
                          color="var(--bulk-success)"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(() => {
                          // If we have many running pods, show simplified view
                          if (node.pods.Running > 10) {
                            return (
                              <Tooltip title={`Running: ${node.pods.Running} pods`}>
                                <Chip
                                  label={`Running: ${node.pods.Running}`}
                                  size="small"
                                  sx={{
                                    bgcolor: `${getStatusColor('Running')}20`,
                                    color: getStatusColor('Running'),
                                    border: `1px solid ${getStatusColor('Running')}40`,
                                    '&:hover': {
                                      bgcolor: `${getStatusColor('Running')}30`,
                                    }
                                  }}
                                />
                              </Tooltip>
                            );
                          }

                          // Otherwise show all statuses
                          return Object.entries(node.pods).map(([status, count]) => {
                            if (status === 'total') return null;
                            if (count === 0) return null;
                            return (
                              <Tooltip
                                key={status}
                                title={`${status}: ${count} pods`}
                              >
                                <Chip
                                  label={`${status}: ${count}`}
                                  size="small"
                                  sx={{
                                    bgcolor: `${getStatusColor(status)}20`,
                                    color: getStatusColor(status),
                                    border: `1px solid ${getStatusColor(status)}40`,
                                    '&:hover': {
                                      bgcolor: `${getStatusColor(status)}30`,
                                    }
                                  }}
                                />
                              </Tooltip>
                            );
                          });
                        })()}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default NodeStatusPanel; 